import type { TreeViewDataItem } from "@patternfly/react-core";
import { type ChangeEvent, type Dispatch, type SetStateAction, useCallback, useState } from "react";
import { CreateProductVersionModalKeys } from "../constants/product";
import type { RedhatAffinityEntity, VersionsEntity } from "../interfaces/graphql";
import type { TreeDataItem } from "./useTreeData";

interface TreeDataResult {
	treeData: unknown[];
	transformedData: TreeDataItem[];
}

interface FormikSetFieldValue {
	setFieldValue: (field: string, value: RedhatAffinityEntity[]) => void;
}

interface UseCheckedItemsReturn {
	checkedItems: RedhatAffinityEntity[];
	setCheckedItems: Dispatch<SetStateAction<RedhatAffinityEntity[]>>;
	onCheck: (event: ChangeEvent, treeViewItem: TreeViewDataItem, formikProps: FormikSetFieldValue) => void;
	isChecked: (dataItem: TreeViewDataItem) => boolean;
	areAllDescendantsChecked: (dataItem: TreeViewDataItem) => boolean;
	areSomeDescendantsChecked: (dataItem: TreeViewDataItem) => boolean;
	mapTree: (item: TreeViewDataItem) => TreeViewDataItem;
}

const flattenTree = (tree: TreeViewDataItem[]): TreeViewDataItem[] => {
	let result: TreeViewDataItem[] = [];
	tree.forEach((item) => {
		result.push(item);
		if (item.children) {
			result = result.concat(flattenTree(item.children));
		}
	});
	return result;
};

export const useCheckedItems = (
	openshiftTreeData: TreeDataResult,
	rhelTreeData: TreeDataResult,
	openShiftFutureVersions: Array<{ id: number; name: string }> = [],
): UseCheckedItemsReturn => {
	const [checkedItems, setCheckedItems] = useState<RedhatAffinityEntity[]>([]);

	const isChecked = useCallback(
		(dataItem: TreeViewDataItem): boolean => {
			const label = dataItem?.checkProps?.["aria-label"];
			if (label === "openShift-version-future") {
				const futureIds = new Set(openShiftFutureVersions.map((v) => v.id));
				return checkedItems?.some(
					(item) => item?.short_name === "RHOCP" && item?.versions?.some((v) => futureIds.has(v.id)),
				);
			} else if (label === "rhel-version-future") {
				return checkedItems?.some((item) => item?.short_name === "RHEL" && item?.support_future_versions);
			} else {
				const itemId = typeof dataItem.id === "string" ? parseInt(dataItem.id, 10) : dataItem.id;
				return checkedItems?.some((item) => item?.versions?.some((version) => version.id === itemId));
			}
		},
		[checkedItems, openShiftFutureVersions],
	);

	const areAllDescendantsChecked = useCallback(
		(dataItem: TreeViewDataItem): boolean =>
			dataItem.children ? dataItem?.children?.every((child) => areAllDescendantsChecked(child)) : isChecked(dataItem),
		[isChecked],
	);

	const areSomeDescendantsChecked = useCallback(
		(dataItem: TreeViewDataItem): boolean =>
			dataItem.children ? dataItem?.children?.some((child) => areSomeDescendantsChecked(child)) : isChecked(dataItem),
		[isChecked],
	);

	const mapTree = useCallback(
		(item: TreeViewDataItem): TreeViewDataItem => {
			const hasCheck = areAllDescendantsChecked(item);
			// Reset checked properties to be updated
			if (item.checkProps) {
				item.checkProps.checked = isChecked(item);
				if (hasCheck) {
					item.checkProps.checked = true;
				} else {
					const hasPartialCheck = areSomeDescendantsChecked(item);
					if (hasPartialCheck) {
						item.checkProps.checked = null;
					}
				}

				if (item.children) {
					return {
						...item,
						children: item.children.map((child) => mapTree(child)),
					};
				}
			}
			return item;
		},
		[areAllDescendantsChecked, areSomeDescendantsChecked, isChecked],
	);

	const onCheck = useCallback(
		(event: ChangeEvent, treeViewItem: TreeViewDataItem, formikProps: FormikSetFieldValue) => {
			const checked = (event.target as HTMLInputElement).checked;
			const flatCheckedItems: TreeViewDataItem[] = flattenTree([treeViewItem]);

			// Object structure needed by pyxis for post API call.
			const pyxisObjectStructure: RedhatAffinityEntity[] = [
				...(openshiftTreeData?.transformedData.map(
					(elm): RedhatAffinityEntity => ({
						id: typeof elm?.id === "number" ? elm.id : Number(elm?.id),
						name: typeof elm?.name === "string" ? elm.name : String(elm?.name),
						short_name: elm?.shortName ?? "",
						support_future_versions:
							checkedItems?.find((item) => item?.short_name === "RHOCP")?.support_future_versions || false,
						versions: checkedItems?.find((item) => item?.short_name === "RHOCP")?.versions ?? [],
					}),
				) || []),
				...(rhelTreeData?.transformedData.map(
					(elm): RedhatAffinityEntity => ({
						id: typeof elm?.id === "number" ? elm.id : Number(elm?.id),
						name: typeof elm?.name === "string" ? elm.name : String(elm?.name),
						short_name: elm?.shortName ?? "",
						support_future_versions:
							checkedItems?.find((item) => item?.short_name === "RHEL")?.support_future_versions || false,
						versions: checkedItems?.find((item) => item?.short_name === "RHEL")?.versions ?? [],
					}),
				) || []),
			];

			//transform the selected data into pyxis object structure.
			const updatedSelectItems: RedhatAffinityEntity[] = pyxisObjectStructure?.map((item) => {
				// Clone versions array for modification
				let rhocpVersions: VersionsEntity[] = [...(item?.short_name === "RHOCP" ? item.versions || [] : [])];
				let rhelVersions: VersionsEntity[] = [...(item?.short_name === "RHEL" ? item.versions || [] : [])];
				let support: boolean = item?.support_future_versions ?? false;

				flatCheckedItems.forEach((versionItem) => {
					const label = versionItem.checkProps?.["aria-label"];
					if (item?.short_name === "RHOCP") {
						if (label === "openShift-version-future") {
							if (checked) {
								for (const fv of openShiftFutureVersions) {
									if (!rhocpVersions.some((v) => v.id === fv.id)) {
										rhocpVersions.push({ id: fv.id, name: fv.name });
									}
								}
							} else {
								const futureIds = new Set(openShiftFutureVersions.map((v) => v.id));
								rhocpVersions = rhocpVersions.filter((v) => !futureIds.has(v.id));
							}
						} else if (label === "openShift-version") {
							const itemId = typeof versionItem.id === "string" ? parseInt(versionItem.id, 10) : versionItem.id;
							if (checked) {
								if (!rhocpVersions.some((version) => version.id === itemId)) {
									rhocpVersions.push({ id: itemId as number, name: String(versionItem.name) });
								}
							} else {
								rhocpVersions = rhocpVersions.filter((version) => version.id !== itemId);
							}
						}
					} else if (item?.short_name === "RHEL") {
						if (label === "rhel-version-future") {
							support = checked;
						} else if (label === "rhel-version") {
							const itemId = typeof versionItem.id === "string" ? parseInt(versionItem.id, 10) : versionItem.id;
							if (checked) {
								if (!rhelVersions.some((version) => version.id === itemId)) {
									rhelVersions.push({ id: itemId as number, name: String(versionItem.name) });
								}
							} else {
								rhelVersions = rhelVersions.filter((version) => version.id !== itemId);
							}
						}
					}
				});

				return {
					...item,
					...(item?.short_name !== "RHOCP" && { support_future_versions: support }),
					versions:
						item?.short_name === "RHOCP"
							? rhocpVersions
							: item?.short_name === "RHEL"
								? rhelVersions
								: (item.versions ?? []),
				};
			});

			setCheckedItems(updatedSelectItems);
			formikProps?.setFieldValue(CreateProductVersionModalKeys?.selectedVersions, updatedSelectItems);
		},
		[checkedItems, openshiftTreeData, rhelTreeData, openShiftFutureVersions],
	);

	return {
		checkedItems,
		setCheckedItems,
		onCheck,
		isChecked,
		areAllDescendantsChecked,
		areSomeDescendantsChecked,
		mapTree,
	};
};
